import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Wallet, Clock, CalendarDays, AlertTriangle, ArrowRight, MessageCircle, CheckCircle2, Cake, Send, Coins } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAppState } from "@/context/AppState";
import { RiskDot } from "@/components/RiskDot";
import { ReminderMessageDialog } from "@/components/ReminderMessageDialog";
import { FelicitacionDialog } from "@/components/FelicitacionDialog";
import {
  formatPrecio,
  formatDiaFecha,
  formatHora,
  isSameDay,
  isPast,
  addDays,
  computeRisk,
  whatsappLink,
  mergeClients,
  daysUntilBirthday,
} from "@/lib/agenda";

export default function Dashboard() {
  const { appointments, clients, updateAppointment } = useAppState();
  const navigate = useNavigate();
  const today = new Date();
  const weekEnd = addDays(today, 7);
  const tomorrow = addDays(today, 1);

  const cumplesHoy = mergeClients(clients, appointments)
    .filter((c) => c.birthday && c.phone && daysUntilBirthday(c.birthday) === 0);
  const [felicitado, setFelicitado] = useState(null);

  const mananaAppts = appointments
    .filter((a) => isSameDay(a.appointment_datetime, tomorrow))
    .sort((a, b) => new Date(a.appointment_datetime) - new Date(b.appointment_datetime));

  const [reminderAppt, setReminderAppt] = useState(null);
  const markSent = (a) => {
    if (!a.reminder_sent) updateAppointment(a.id, { reminder_sent: true });
  };

  const todayAppts = appointments.filter((a) => isSameDay(a.appointment_datetime, today));
  const weekAppts = appointments.filter((a) => {
    const d = new Date(a.appointment_datetime);
    return d >= today && d <= weekEnd;
  });
  const cobradoHoy = todayAppts
    .filter((a) => a.payment_status === "pagado")
    .reduce((s, a) => s + (a.price || 0), 0);
  const pendienteTotal = appointments
    .filter((a) => a.payment_status === "pendiente")
    .reduce((s, a) => s + (a.price || 0), 0);
  const pendientesPasadas = appointments
    .filter((a) => a.payment_status === "pendiente" && isPast(a.appointment_datetime))
    .sort((a, b) => new Date(a.appointment_datetime) - new Date(b.appointment_datetime));

  const paidAppts = appointments.filter((a) => a.payment_status === "pagado");
  const incomeMes = paidAppts
    .filter((a) => {
      const d = new Date(a.appointment_datetime);
      return d.getFullYear() === today.getFullYear() && d.getMonth() === today.getMonth();
    })
    .reduce((s, a) => s + (a.price || 0), 0);
  const incomeAno = paidAppts
    .filter((a) => new Date(a.appointment_datetime).getFullYear() === today.getFullYear())
    .reduce((s, a) => s + (a.price || 0), 0);
  const diezmoMes = incomeMes * 0.1;
  const diezmoAno = incomeAno * 0.1;

  const proximas = appointments
    .filter((a) => !isPast(a.appointment_datetime))
    .sort((a, b) => new Date(a.appointment_datetime) - new Date(b.appointment_datetime))
    .slice(0, 4);

  return (
    <div className="space-y-8 animate-fade-up" data-testid="dashboard-page">
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900">Hola, estás en tu agenda</h1>
        <p className="text-sm text-slate-500 mt-1">
          Aquí tienes tu día y tus cobros en un vistazo.
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard icon={Wallet} label="Cobrado hoy" value={formatPrecio(cobradoHoy)} tone="paid" />
        <StatCard icon={Clock} label="Pendiente de cobro" value={formatPrecio(pendienteTotal)} tone="pending" />
        <StatCard icon={CalendarDays} label="Citas hoy" value={todayAppts.length} />
        <StatCard icon={CalendarDays} label="Esta semana" value={weekAppts.length} />
      </div>

      <div data-testid="contabilidad-section">
        <h2 className="font-heading text-lg font-semibold text-slate-900 mb-3">Contabilidad</h2>
        <button
          type="button"
          data-testid="diezmo-button"
          className="w-full text-left rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 transition-transform duration-300 hover:-translate-y-0.5"
          style={{ backgroundColor: "#2E2A8F", border: "1px solid #E3B341", boxShadow: "0 18px 44px -20px rgba(46,42,143,0.6)" }}
        >
          <div className="flex items-center gap-3">
            <span className="flex items-center justify-center h-11 w-11 rounded-xl" style={{ backgroundColor: "rgba(227,179,65,0.16)", color: "#E3B341" }}>
              <Coins className="h-5 w-5" />
            </span>
            <div>
              <p className="font-heading text-lg font-bold text-white">Diezmo</p>
              <p className="text-xs" style={{ color: "#C8C4E8" }}>10% de tus ingresos cobrados</p>
            </div>
          </div>
          <div className="flex gap-8 sm:gap-10">
            <div>
              <p className="text-[11px] uppercase tracking-wide" style={{ color: "#E3B341" }}>Este mes</p>
              <p className="font-heading text-2xl font-bold text-white" data-testid="diezmo-mes">{formatPrecio(diezmoMes)}</p>
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-wide" style={{ color: "#E3B341" }}>Este año</p>
              <p className="font-heading text-2xl font-bold text-white" data-testid="diezmo-ano">{formatPrecio(diezmoAno)}</p>
            </div>
          </div>
        </button>
      </div>

      {cumplesHoy.length > 0 && (
        <div className="rounded-2xl border border-[#D7CBF0] bg-[#F3EDE4] p-5" data-testid="birthdays-today">
          <div className="flex items-center gap-2" style={{ color: "#2E2A8F" }}>
            <Cake className="h-5 w-5" />
            <h2 className="font-heading font-semibold">Cumpleaños de hoy</h2>
          </div>
          <p className="text-sm text-slate-600 mt-1">
            {cumplesHoy.length === 1 ? "Hoy cumple años una persona." : `Hoy cumplen años ${cumplesHoy.length} personas.`} Felicítala con una frase de la IA.
          </p>
          <div className="mt-4 space-y-2">
            {cumplesHoy.map((c) => (
              <div
                key={c.phone || c.name}
                className="flex items-center justify-between gap-3 bg-white rounded-xl px-4 py-3 border border-[#E6DEF5]"
                data-testid={`birthday-today-${c.phone || c.name}`}
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-900 truncate">Hoy cumple años {c.name}</p>
                  <p className="text-xs text-slate-500">{c.phone}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setFelicitado(c)}
                  data-testid={`felicitar-today-${c.phone || c.name}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium text-white shrink-0"
                  style={{ backgroundColor: "#275FBE" }}
                >
                  <Send className="h-3.5 w-3.5" /> Felicitar
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {pendientesPasadas.length > 0 && (
        <div
          className="rounded-2xl border border-amber-200 bg-amber-50 p-5"
          data-testid="pending-alert"
        >
          <div className="flex items-center gap-2 text-amber-800">
            <AlertTriangle className="h-5 w-5" />
            <h2 className="font-heading font-semibold">Citas pasadas sin pagar</h2>
          </div>
          <p className="text-sm text-amber-700 mt-1">
            Tienes {pendientesPasadas.length} citas que ya pasaron y siguen pendientes.
          </p>
          <div className="mt-4 space-y-2">
            {pendientesPasadas.map((a) => (
              <div
                key={a.id}
                className="flex items-center justify-between bg-white rounded-xl px-4 py-3 border border-amber-100"
                data-testid={`pending-item-${a.id}`}
              >
                <div>
                  <p className="text-sm font-medium text-slate-900">{a.client_name}</p>
                  <p className="text-xs text-slate-500">{formatDiaFecha(a.appointment_datetime)}</p>
                </div>
                <span className="text-sm font-semibold text-amber-700">{formatPrecio(a.price)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {mananaAppts.length > 0 && (
        <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="tomorrow-reminders">
          <div className="flex items-center gap-2 text-slate-800">
            <MessageCircle className="h-5 w-5" style={{ color: "#0E9F6E" }} />
            <h2 className="font-heading font-semibold">Recordatorios de mañana</h2>
          </div>
          <p className="text-sm text-slate-500 mt-1">
            Envía el recordatorio por WhatsApp a las citas de mañana.
          </p>
          <div className="mt-4 space-y-2">
            {mananaAppts.map((a) => (
              <div key={a.id} className="flex items-center justify-between gap-3 bg-slate-50 rounded-xl px-4 py-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-900 truncate">{a.client_name}</p>
                  <p className="text-xs text-slate-500">{formatHora(a.appointment_datetime)} · {a.client_phone}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setReminderAppt(a)}
                  data-testid={`send-reminder-${a.id}`}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium shrink-0 ${
                    a.reminder_sent ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "text-white"
                  }`}
                  style={a.reminder_sent ? {} : { backgroundColor: "#0E9F6E" }}
                >
                  {a.reminder_sent ? <CheckCircle2 className="h-3.5 w-3.5" /> : <MessageCircle className="h-3.5 w-3.5" />}
                  {a.reminder_sent ? "Enviado" : "Enviar"}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading text-lg font-semibold text-slate-900">Próximas citas</h2>
          <Button
            variant="ghost"
            className="text-sm gap-1"
            style={{ color: "#275FBE" }}
            onClick={() => navigate("/app/agenda")}
            data-testid="go-agenda-button"
          >
            Ver agenda <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
        <div className="space-y-2">
          {proximas.map((a) => (
            <div
              key={a.id}
              className="flex items-center justify-between bg-white rounded-xl px-4 py-3 border border-[#EAE5DF] hover:shadow-[0_8px_24px_-14px_rgba(46,42,143,0.16)] transition-all duration-300"
            >
              <div className="flex items-center gap-3">
                <span className="text-xs font-medium text-slate-500 w-20">
                  {formatHora(a.appointment_datetime)}
                </span>
                <div>
                  <div className="flex items-center gap-2">
                    <RiskDot risk={computeRisk(a, appointments)} size={9} />
                    <p className="text-sm font-medium text-slate-900">{a.client_name}</p>
                  </div>
                  <p className="text-xs text-slate-500">{formatDiaFecha(a.appointment_datetime)}</p>
                </div>
              </div>
              <span
                className={`text-xs font-medium px-2.5 py-1 rounded-full border ${
                  a.payment_status === "pagado"
                    ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                    : "bg-amber-50 border-amber-200 text-amber-700"
                }`}
              >
                {a.payment_status === "pagado" ? "Pagado" : "Pendiente"}
              </span>
            </div>
          ))}
        </div>
      </div>

      <ReminderMessageDialog
        open={!!reminderAppt}
        appointment={reminderAppt}
        onClose={() => setReminderAppt(null)}
        onSent={(a) => markSent(a)}
      />

      <FelicitacionDialog
        open={!!felicitado}
        target={felicitado}
        kind="cumpleanos"
        onClose={() => setFelicitado(null)}
      />
    </div>
  );
}

const StatCard = ({ icon: Icon, label, value, tone }) => {
  const bg =
    tone === "paid" ? "#ECFDF5" : tone === "pending" ? "#FFFBEB" : "#EAF1FB";
  const color =
    tone === "paid" ? "#047857" : tone === "pending" ? "#B45309" : "#275FBE";
  return (
    <div className="bg-white rounded-2xl border border-[#EAE5DF] p-4 shadow-[0_6px_24px_-14px_rgba(46,42,143,0.10)] hover:shadow-[0_12px_28px_-12px_rgba(46,42,143,0.16)] transition-all duration-300">
      <span
        className="flex items-center justify-center h-9 w-9 rounded-xl"
        style={{ backgroundColor: bg, color }}
      >
        <Icon className="h-4.5 w-4.5" />
      </span>
      <p className="mt-3 text-xs text-slate-500">{label}</p>
      <p className="font-heading text-xl font-bold text-slate-900 mt-0.5">{value}</p>
    </div>
  );
};
